import React, { useMemo, useState, useEffect, useCallback, CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_SortingState,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_ExpandedState,
} from 'material-react-table';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import SearchBar from 'components/UI/Search';
import Switch from 'components/UI/Switch';
import Select from 'components/UI/Select';
import Pagination from 'components/UI/Pagination';
import MatTableColumnsButton from 'components/MaterialTable/MatTableColumnsButton';
import ActionMenu from '../UI/actionMenu';
import { FontSize } from 'styles/themeSizes';
import { Text, Background, Border } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { Height } from 'styles/themeHeights';
import { FontWeight } from 'lib/styles';
import { emptyDataSourceExtraMessage, emptyDataSourceMainMessage } from '../../constants/errorMessages';
import {
  MatDataGridProps,
  LegacyColumn,
  DetailPanelDef,
} from './MatDataGridTypes';
import {
  ensureMRTColumns,
  getColumnVisibility,
  getInitialSorting,
  isLegacyColumnArray,
} from './columnAdapter';

const TableWrapper = styled.div<any>`
  max-width: 100%;
  width: ${({ $wrapperWidth }) => $wrapperWidth};

  @media (min-width: 1400px) {
    width: 100%;
  }

  ${({ $disableBorderCollapse }) => $disableBorderCollapse && css`
    table {
      border-collapse: separate !important;
    }
 `}

  ${({ $customHeaderBorders }) => $customHeaderBorders && css`
    table thead tr th {
      border-right: none !important;
      padding: 4px 2px 4px 16px !important;
    }

    table thead tr{
      th:last-child{
        padding: 4px 0px 4px 16px !important;
        :after{
          display: none !important;
        }
      }
    }

    table thead tr th:after {
      position: absolute;
      content: "";
      top: 3px;
      height: 24px;
      border: 1px solid #C1C9D1;
    }

    table thead tr th span{
      width: 100%
    }
 `}

  /* Override MRT header styles to match original design */
  .MuiTableHead-root {
    .MuiTableCell-root {
      background-color: ${Background.white} !important;
      color: ${Text.primary} !important;
      font-family: 'Aeonik', sans-serif !important;
      font-weight: 400 !important;
      font-size: ${FontSize.medium} !important;
      border-bottom: 1px solid ${Border.light} !important;
      border-right: none !important;
      line-height: 20px !important;
    }
  }

  /* Ensure all Material React Table headers use Aeonik font */
  .mrt-table-head-cell,
  .MuiTableHead-root th,
  .MuiTableHead-root .MuiTableCell-head,
  table thead tr th,
  table thead tr th * {
    font-family: 'Aeonik', sans-serif !important;
  }

  /* Reduce checkbox column width */
  .mrt-row-select:has(input[type="checkbox"]),
  .mrt-table-head-cell-select,
  .mrt-table-body-cell-select,
  table thead tr th:first-child,
  table tbody tr td:first-child,
  .MuiTableHead-root .MuiTableCell-head:first-child,
  .MuiTableBody-root .MuiTableCell-body:first-child,
  th[data-column-id="mrt-row-select"],
  td[data-column-id="mrt-row-select"] {
    width: 30px !important;
    min-width: 30px !important;
    max-width: 30px !important;
    padding: 4px !important;
  }

  /* Reduce image column width - only target by column ID, not position */
  table thead tr th[data-column-id="imagePath"],
  table tbody tr td[data-column-id="imagePath"],
  .MuiTableHead-root .MuiTableCell-head[data-column-id="imagePath"],
  .MuiTableBody-root .MuiTableCell-body[data-column-id="imagePath"] {
    width: 40px !important;
    min-width: 40px !important;
    max-width: 40px !important;
    padding: 4px 12px !important;
    text-overflow: clip !important;
    overflow: visible !important;
  }

  /* Override MRT row styles - white background, bottom border only */
  .MuiTableBody-root {
    .MuiTableRow-root {
      background-color: ${Background.white} !important;

      &:hover {
        background-color: ${Background.white} !important;
        
        td {
          background-color: ${Background.white} !important;
        }
      }

      /* Remove selection highlighting */
      &.Mui-selected,
      &[aria-selected="true"] {
        background-color: ${Background.white} !important;
        
        td {
          background-color: ${Background.white} !important;
        }
      }

      /* Remove selection hover highlighting */
      &.Mui-selected:hover,
      &[aria-selected="true"]:hover {
        background-color: ${Background.white} !important;
        
        td {
          background-color: ${Background.white} !important;
        }
      }
    }
  }

  /* Override MRT checkbox styles */
  .MuiCheckbox-root {
    padding: 4px !important;

    /* Indeterminate state (select-all when some are selected) */
    &.MuiCheckbox-indeterminate {
      color: #767676 !important;
    }

    &.Mui-checked {
      color: #FC780B !important;
    }
  }

  /* Aggressive override for any row highlighting */
  tr.MuiTableRow-root,
  .MuiTableRow-root,
  tbody tr,
  tr {
    background-color: ${Background.white} !important;
    
    &:hover,
    &.Mui-hover,
    &.MuiTableRow-hover,
    &:focus,
    &:active {
      background-color: ${Background.white} !important;
      background: ${Background.white} !important;
    }
    
    &.Mui-selected,
    &[aria-selected="true"],
    &.MuiTableRow-selected,
    &[data-selected="true"] {
      background-color: ${Background.white} !important;
      background: ${Background.white} !important;
      
      &:hover {
        background-color: ${Background.white} !important;
        background: ${Background.white} !important;
      }
      
      td, th {
        background-color: ${Background.white} !important;
        background: ${Background.white} !important;
      }
    }
  }

  /* Override Material React Table specific classes */
  .mrt-row-selected,
  .mrt-table-body-row-highlighted,
  .mrt-table-body-row-hover,
  .mrt-table-body-row:hover {
    background-color: ${Background.white} !important;
    background: ${Background.white} !important;
  }

  /* Override all td hover states */
  td.MuiTableCell-root,
  .MuiTableCell-body {
    background-color: ${Background.white} !important;
    background: ${Background.white} !important;
    
    &:hover {
      background-color: ${Background.white} !important;
      background: ${Background.white} !important;
    }
  }

  /* Force all table rows and cells to white with maximum specificity */
  && {
    .MuiTable-root,
    .MuiTableBody-root,
    .MuiTableRow-root,
    .MuiTableCell-root,
    table,
    tbody,
    tr,
    td {
      background-color: ${Background.white} !important;
      background: ${Background.white} !important;
    }
  }

  /* Fix empty state width and positioning */
  .MuiTableBody-root {
    width: 100% !important;
    
    .MuiTableRow-root {
      width: 100% !important;
      
      .MuiTableCell-root {
        &[colspan] {
          width: 100% !important;
          min-width: 100% !important;
          max-width: none !important;
          text-align: center !important;
          vertical-align: middle !important;
          left: 0 !important;
          right: 0 !important;
          margin: 0 auto !important;
        }
      }
    }
  }

  /* Ensure table and wrapper take full width */
  .MuiTable-root {
    width: 100% !important;
    margin: 0 !important;
  }

  /* Override any Material React Table container constraints */
  .mrt-table-container,
  .MuiTableContainer-root {
    width: 100% !important;
    overflow-x: visible !important;
  }

  /* Force full width on table wrapper */
  && {
    width: 100% !important;
    min-width: 100% !important;
    
    > div {
      width: 100% !important;
    }
  }

  /* Style the default Material React Table empty state */
  .MuiBox-root {
    &[role="alert"] {
      width: 100% !important;
      text-align: center !important;
      padding: 60px 40px !important;
      min-height: 300px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      
      .MuiTypography-root {
        font-size: ${FontSize.medium} !important;
        color: ${Text.secondary} !important;
        width: 100% !important;
      }
    }
  }

  /* Alternative selector for empty state */
  .mrt-table-empty-row,
  .MuiTableCell-root:only-child {
    text-align: center !important;
    padding: 60px 40px !important;
    width: 100% !important;
  }

  .MuiBox-root{
    min-height: 0rem;
    padding: 0;
  }

  table tbody tr td {
    vertical-align: top !important;
    cursor: pointer;
    word-wrap: break-word;
    font-size: 14px;
    line-height: 20px;
    color: ${Text.primary};
  }

  table thead tr th span[tabindex='-1'] {
    visibility: hidden !important;
  }

  th {
    font-family: 'Aeonik', sans-serif !important;
    
    &:first-child {
      ${({ $detailPanel }) => $detailPanel && css`
        border-right: none !important;
      `}

      ${({ $borderRadius }) => $borderRadius && css`
        border-radius: ${BorderRadius.m} 0 0 0;
      `}
    }

    &:last-child {
      border-right: none !important;

      ${({ $borderRadius }) => $borderRadius && css`
        border-radius: 0 ${BorderRadius.m} 0 0;
      `}
    }
  }
`;

const defaultHeaderStyle: CSSProperties = {
  padding: `${Space.spHalf} ${Space.spBase} ${Space.spHalf} ${Space.sp2x}`,
  textAlign: 'left',
  backgroundColor: Background.shaded,
  color: Text.primary,
  cursor: 'pointer',
  fontFamily: "'Aeonik', sans-serif",
  fontWeight: FontWeight.bold,
  fontSize: FontSize.medium,
  lineHeight: '20px',
  borderRight: `1px solid ${Border.main}`,
  minHeight: `${Height.l}`
};

const defaultRowStyle = (data: any, index: number): CSSProperties => {
  const hasMoreThanOneConsignments = data?.consignments?.length > 1;
  const isExpanded = data?._isExpanded;
  const highlightedRow = hasMoreThanOneConsignments && isExpanded;

  return {
    padding: `${Space.sp2x} ${Space.spBase} ${Space.sp2x} ${Space.sp2x}`,
    borderBottom: `1px solid ${highlightedRow ? Border.brand : Border.light}`,
    borderTop: `1px solid ${highlightedRow ? Border.brand : Border.light}`,
    borderRight: highlightedRow ? `1px solid ${Border.brand}` : undefined,
    borderLeft: highlightedRow ? `1px solid ${Border.brand}` : undefined,
    fontSize: '14px',
    lineHeight: '20px',
    backgroundColor: Background.white, // Always white, no alternating colors or highlighting
    verticalAlign: 'top'
  };
};

function MatDataGrid<TData extends Record<string, any> = any>({
  columns,
  data,
  detailPanel,
  emptyDataSourceMessage = emptyDataSourceExtraMessage,
  emptyDataSourceHeader = emptyDataSourceMainMessage,
  addRemoveColumns = 'VIEW COLUMNS',
  title,
  wrapperWidth = '100%',
  search = false,
  paging = false,
  sorting = true,
  toolbar = false,
  switchers,
  selectors,
  customSearch,
  customPagination,
  isLoading = false,
  columnsButton = false,
  draggable = false,
  headerStyle: headerStyleProp = defaultHeaderStyle,
  rowStyle: rowStyleProp = defaultRowStyle,
  tableRef,
  onRowClick,
  onOrderChange,
  onColumnDragged,
  onChangeColumnHidden,
  overrideToolbar = false,
  onUpdateColumns,
  onToggleDetailPanel,
  initialColumns,
  thirdSortClick = false,
  borders,
  customHeaderBorders,
  actionsColumnIndex,
  actionMenu,
  disableBorderCollapse = false,
  borderBetweenRows = true,
  enableSelection = false,
  onSelectionChange,
}: MatDataGridProps<TData>) {
  // Convert legacy columns to MRT format
  const mrtColumns = useMemo(() => {
    const converted = ensureMRTColumns<TData>(columns);

    // Add actions column if actionMenu is provided
    if (actionMenu && actionMenu.length > 0) {
      const actionsColumn: MRT_ColumnDef<TData> = {
        id: 'mrt-row-actions',
        header: '',
        size: 50,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <ActionMenu actionMenu={actionMenu} data={row.original} />
        ),
      };

      if (actionsColumnIndex !== undefined && actionsColumnIndex >= 0) {
        const result = [...converted];
        result.splice(actionsColumnIndex, 0, actionsColumn);
        return result;
      }

      return [...converted, actionsColumn];
    }

    return converted;
  }, [columns, actionMenu, actionsColumnIndex]);

  // Get initial column visibility from legacy columns
  const initialColumnVisibility = useMemo(() => {
    if (isLegacyColumnArray(columns)) {
      return getColumnVisibility(columns as LegacyColumn[]);
    }
    return {};
  }, [columns]);

  // Get initial sorting from legacy columns
  const initialSorting = useMemo(() => {
    if (isLegacyColumnArray(columns)) {
      return getInitialSorting(columns as LegacyColumn[]);
    }
    return [];
  }, [columns]);

  // State
  const [sortingState, setSortingState] = useState<MRT_SortingState>(initialSorting);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(initialColumnVisibility);
  const [expandedRows, setExpandedRows] = useState<MRT_ExpandedState>({});

  // Update column visibility when columns prop changes
  useEffect(() => {
    if (isLegacyColumnArray(columns)) {
      const newVisibility = getColumnVisibility(columns as LegacyColumn[]);
      setColumnVisibility(newVisibility);
    }
  }, [columns]);

  // Handle sorting changes
  const handleSortingChange = useCallback((updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sortingState) : updaterOrValue;

    // Handle third click to clear sorting
    if (thirdSortClick && sortingState.length > 0 && newSorting.length > 0) {
      const currentSort = sortingState[0];
      const newSort = newSorting[0];

      if (currentSort.id === newSort.id && currentSort.desc && newSort.desc) {
        setSortingState([]);
        if (onOrderChange) {
          onOrderChange(-1, '');
        }
        return;
      }
    }

    setSortingState(newSorting);

    if (onOrderChange && newSorting.length > 0) {
      const sort = newSorting[0];
      const legacyColumns = isLegacyColumnArray(columns) ? columns as LegacyColumn[] : [];
      const columnIndex = legacyColumns.findIndex(col => col.field === sort.id);
      onOrderChange(columnIndex, sort.desc ? 'desc' : 'asc');
    }
  }, [sortingState, thirdSortClick, onOrderChange, columns]);

  // Handle row selection changes
  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = Object.keys(rowSelection)
        .filter(key => rowSelection[key])
        .map(index => data[parseInt(index, 10)])
        .filter(Boolean);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, data]);

  // Custom toolbar elements
  const searchBar = customSearch ? (
    <SearchBar
      placeholder={customSearch.placeholder}
      searchValue={customSearch.value}
      onSearch={customSearch.onChange}
      containerWidth="320px"
    />
  ) : null;

  const switchComponent = switchers
    ? switchers.map((switcher, index) => (
      <Switch
        id={`custom-switch-${index}`}
        key={index}
        label={switcher.label}
        checked={switcher.checked}
        onChange={switcher.onChange}
      />
    ))
    : null;

  const selectComponent = selectors
    ? selectors.map((selector, index) => (
      <Select
        key={index}
        options={selector.options}
        value={selector.value}
        onChange={selector.onChange}
        width={selector.width || '100%'}
        containerWidth={'auto'}
      />
    ))
    : null;

  const tableColumnsButton = initialColumns && onUpdateColumns ? (
    <MatTableColumnsButton
      columns={columns as any[]}
      onUpdateColumns={onUpdateColumns}
      initialColumns={initialColumns as any[]}
    />
  ) : null;

  const customToolbarContent = (
    <FlexboxContainer
      justifyContent={customSearch ? 'space-between' : 'flex-end'}
      padding={Space.spBase}
      alignItems="center"
      bgColor={Background.shaded}
      borderRadius={`${BorderRadius.m} ${BorderRadius.m} 0 0`}
    >
      {searchBar}
      <FlexboxContainer justifyContent="flex-end" alignItems="center">
        {switchComponent}
        {tableColumnsButton}
        {selectComponent}
      </FlexboxContainer>
    </FlexboxContainer>
  );

  // Handle detail panel rendering
  const renderDetailPanel = useMemo(() => {
    if (!detailPanel) return undefined;

    if (typeof detailPanel === 'function') {
      return ({ row, table }: { row: MRT_Row<TData>; table: MRT_TableInstance<TData> }) => {
        return (detailPanel as (row: MRT_Row<TData>) => React.ReactNode)(row);
      };
    }

    if (Array.isArray(detailPanel) && detailPanel.length > 0) {
      return ({ row, table }: { row: MRT_Row<TData>; table: MRT_TableInstance<TData> }) => {
        const panel = detailPanel[0] as DetailPanelDef;
        const isDisabled = typeof panel.disabled === 'function'
          ? panel.disabled(row.original)
          : panel.disabled;

        if (isDisabled) return null;

        return panel.render(row.original);
      };
    }

    return undefined;
  }, [detailPanel]);

  // Get row style based on index and data
  const getRowStyle = useCallback((row: MRT_Row<TData>, index: number): CSSProperties => {
    // Always return empty object to prevent any custom row styling
    return {};
  }, []);


  // Create table instance
  const table = useMaterialReactTable({
    columns: mrtColumns,
    data: data || [],

    // State
    state: {
      isLoading,
      sorting: sortingState,
      rowSelection,
      columnVisibility,
      expanded: expandedRows,
    },

    // State handlers
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpandedRows,

    // Features
    enableGlobalFilter: search,
    enablePagination: paging && !customPagination,
    enableSorting: sorting,
    enableTopToolbar: toolbar && !overrideToolbar,
    enableColumnOrdering: draggable,
    enableRowSelection: enableSelection,
    enableExpanding: !!detailPanel,
    enableExpandAll: false,
    enableRowVirtualization: false,

    // Detail panel
    renderDetailPanel: renderDetailPanel,

    // Empty state
    renderEmptyRowsFallback: undefined,

    // Row styling
    muiTableBodyRowProps: ({ row }) => ({
      onClick: onRowClick ? (event) => onRowClick(event, row.original) : undefined,
      style: {
        backgroundColor: Background.white,
      },
      sx: {
        cursor: onRowClick ? 'pointer' : 'default',
        backgroundColor: `${Background.white} !important`,
        '&:hover': {
          backgroundColor: `${Background.white} !important`,
        },
        '&.Mui-selected': {
          backgroundColor: `${Background.white} !important`,
        },
        '&.Mui-selected:hover': {
          backgroundColor: `${Background.white} !important`,
        },
        '&[aria-selected="true"]': {
          backgroundColor: `${Background.white} !important`,
        },
        '&[aria-selected="true"]:hover': {
          backgroundColor: `${Background.white} !important`,
        },
        '& td': {
          borderBottom: borderBetweenRows ? `1px solid ${Border.light}` : 'none',
          fontSize: '14px !important',
          backgroundColor: `${Background.white} !important`,
        },
        '& td:hover': {
          backgroundColor: `${Background.white} !important`,
        },
      },
    }),

    // Header styling
    muiTableHeadCellProps: ({ column }) => {
      const columnProps = column.columnDef.muiTableHeadCellProps;
      const columnSx = typeof columnProps === 'function' ? {} : (columnProps?.sx || {});

      // Add !important to color if it exists in column styles
      const modifiedColumnSx = { ...columnSx } as any;
      if (modifiedColumnSx.color) {
        modifiedColumnSx.color = `${modifiedColumnSx.color} !important`;
      }

      return {
        sx: {
          ...headerStyleProp,
          fontFamily: "'Aeonik', sans-serif !important",
          // Apply individual column header styles if they exist
          ...modifiedColumnSx,
        },
      };
    },

    // Body cell styling
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        ...getRowStyle(row, row.index),
      },
    }),

    // Paper/container styling
    muiTablePaperProps: {
      elevation: 0,
      sx: borders ? { border: '2px solid #E1E6EB' } : {},
    },


    // Override MUI table props to ensure no highlighting
    muiTableProps: {
      sx: {
        '& .MuiTableRow-root': {
          '&:hover': { backgroundColor: Background.white },
          '&.Mui-selected': { backgroundColor: Background.white },
          '&.Mui-selected:hover': { backgroundColor: Background.white },
        },
      },
    },

    // Loading overlay
    muiCircularProgressProps: {
      size: 40,
    },

    // Column ordering callback
    onColumnOrderChange: draggable && onColumnDragged
      ? (updater) => {
        // MRT uses different column order format, need to convert
        // This is a simplified version - full implementation would need to track indices
      }
      : undefined,

    // Localization
    localization: {
      noRecordsToDisplay: emptyDataSourceMessage,
      expand: '',
    },

    // Display column options (for selection, actions, etc.)
    displayColumnDefOptions: {
      'mrt-row-select': {
        size: 40,
        minSize: 40,
        maxSize: 40,
        muiTableHeadCellProps: {
          sx: {
            width: '40px',
            minWidth: '40px',
            maxWidth: '40px',
            padding: '4px',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            width: '40px',
            minWidth: '40px',
            maxWidth: '40px',
            padding: '4px',
          },
        },
      },
    },

    // Initial state
    initialState: {
      showGlobalFilter: search,
      density: 'comfortable',
    },

    // Layout
    layoutMode: 'semantic',

    // Enable column actions only if columnsButton is true
    enableColumnActions: columnsButton,
  });

  // Update table ref
  useEffect(() => {
    if (tableRef) {
      tableRef.current = table;
    }
  }, [table, tableRef]);

  // Show loading indicator if loading
  if (isLoading && data.length === 0) {
    return <LoadingIndicator center />;
  }

  return (
    <TableWrapper
      $borderRadius={!toolbar && !overrideToolbar}
      $wrapperWidth="100%"
      $detailPanel={!!detailPanel}
      $customHeaderBorders={customHeaderBorders}
      $disableBorderCollapse={disableBorderCollapse}
      style={{ width: '100%', minWidth: '100%' }}
    >
      {overrideToolbar && customToolbarContent}
      <MaterialReactTable table={table} />
      {customPagination && <Pagination {...customPagination} />}
    </TableWrapper>
  );
}

export default React.memo(MatDataGrid) as typeof MatDataGrid;
